/**
 * Open enrollment window for an account, shown on the Group Info tab.
 *
 * The window is stored on the linked group's `GroupMetadata` and is what drives
 * the "Open Enrollment" badge on the account header, the kanban card, and the
 * accounts table row. Everyone with access to the account can read it here;
 * only client admins and internal admins get the editing controls (mirroring
 * `isAccountsAdmin` on the server).
 */

import {useEffect, useState, type ReactElement} from 'react';
import {CalendarClock, Check, Edit, Loader2, Trash2, X} from 'lucide-react';
import {Button} from '@common-web/components/ui/button';
import {Card} from '@common-web/components/ui/card';
import {Badge} from '@common-web/components/ui/badge';
import {Input} from '@common-web/components/ui/input';
import {trpc} from '@common-web/lib/trpc';
import {toast} from '@common-web/hooks/use-toast';
import {formatDate} from '@common/lib/utils';
import {
  formatOpenEnrollmentRange,
  resolveOpenEnrollmentStatus,
  type OpenEnrollmentStatus,
  type OpenEnrollmentWindow,
} from '@common/lib/openEnrollment';

interface OpenEnrollmentCardProps {
  accountsId: string;
  /** Null when the linked group has no metadata row yet. */
  enrollmentWindow: OpenEnrollmentWindow | null;
  canEdit: boolean;
}

/** `YYYY-MM-DD` for a `<input type='date'>`, read in UTC to match how the date was stored. */
function toDateInputValue(value: Date | string | null | undefined): string {
  if (!value) return '';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '';
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${d.getUTCFullYear()}-${mm}-${dd}`;
}

const STATUS_BADGE: Record<
  OpenEnrollmentStatus,
  {label: string; variant: 'success' | 'info' | 'gray'} | null
> = {
  ACTIVE: {label: 'Active', variant: 'success'},
  UPCOMING: {label: 'Upcoming', variant: 'info'},
  ENDED: {label: 'Ended', variant: 'gray'},
  INCOMPLETE: null,
  NONE: null,
};

export function OpenEnrollmentCard({
  accountsId,
  enrollmentWindow,
  canEdit,
}: OpenEnrollmentCardProps): ReactElement {
  const utils = trpc.useUtils();
  const startDate = enrollmentWindow?.startDate ?? null;
  const endDate = enrollmentWindow?.endDate ?? null;
  // The saved window as `YYYY-MM-DD`. Depending on these strings rather than the
  // Date objects keeps the sync effect from resetting a half-typed edit whenever
  // the account query refetches and hands back new Date instances.
  const savedStart = toDateInputValue(startDate);
  const savedEnd = toDateInputValue(endDate);

  const [isEditing, setIsEditing] = useState(false);
  const [draftStart, setDraftStart] = useState(savedStart);
  const [draftEnd, setDraftEnd] = useState(savedEnd);
  const [error, setError] = useState<string | null>(null);

  // Sync saved → draft, but never while the admin is mid-edit: a background
  // refetch that changes the saved window must not stomp a half-typed draft.
  // When editing ends the effect re-runs and picks the saved values back up.
  useEffect(() => {
    if (isEditing) return;
    setDraftStart(savedStart);
    setDraftEnd(savedEnd);
  }, [savedStart, savedEnd, isEditing]);

  const mutation = trpc.account.setOpenEnrollmentWindow.useMutation({
    onSuccess: (_data, variables) => {
      toast({
        title: 'Success',
        description: variables.startDate
          ? 'Open enrollment window updated'
          : 'Open enrollment window removed',
      });
      utils.account.getById.invalidate({accountsId});
      setIsEditing(false);
      setError(null);
    },
    onError: (err) => {
      toast({
        title: 'Error',
        description: err.message || 'Failed to update the open enrollment window',
        variant: 'destructive',
      });
    },
  });

  const status = resolveOpenEnrollmentStatus({startDate, endDate});
  const statusBadge = STATUS_BADGE[status];
  const range = formatOpenEnrollmentRange({startDate, endDate});

  function save() {
    if (!draftStart || !draftEnd) {
      setError('Enter both a start and an end date.');
      return;
    }
    if (draftEnd < draftStart) {
      setError('The end date must be on or after the start date.');
      return;
    }
    setError(null);
    mutation.mutate({accountsId, startDate: draftStart, endDate: draftEnd});
  }

  function cancel() {
    setDraftStart(savedStart);
    setDraftEnd(savedEnd);
    setError(null);
    setIsEditing(false);
  }

  return (
    <Card className='p-6'>
      <div className='mb-5 flex items-center justify-between gap-4'>
        <div className='flex items-center gap-3'>
          <div className='flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald/10'>
            <CalendarClock className='h-[18px] w-[18px] text-emerald' />
          </div>
          <h3 className='text-base font-bold text-foreground'>Open Enrollment</h3>
          {statusBadge ? (
            <Badge variant={statusBadge.variant} size='sm'>
              {statusBadge.label}
            </Badge>
          ) : null}
        </div>
        {canEdit && !isEditing ? (
          <div className='flex items-center gap-2'>
            <Button variant='outline' size='sm' onClick={() => setIsEditing(true)}>
              <Edit className='mr-1 h-3 w-3' />
              {status === 'NONE' ? 'Set dates' : 'Edit dates'}
            </Button>
            {status !== 'NONE' ? (
              <Button
                variant='ghost'
                size='sm'
                disabled={mutation.isPending}
                onClick={() => mutation.mutate({accountsId, startDate: null, endDate: null})}
              >
                {mutation.isPending ? (
                  <Loader2 className='mr-1 h-3 w-3 animate-spin' />
                ) : (
                  <Trash2 className='mr-1 h-3 w-3' />
                )}
                Remove
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      {isEditing ? (
        <div className='space-y-3'>
          <div className='flex flex-wrap items-end gap-4'>
            <div>
              <label
                htmlFor='oe-start'
                className='mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground'
              >
                Start date
              </label>
              <Input
                id='oe-start'
                type='date'
                className='h-9 w-44'
                value={draftStart}
                disabled={mutation.isPending}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? 'oe-range-error' : undefined}
                onChange={(e) => setDraftStart(e.target.value)}
              />
            </div>
            <div>
              <label
                htmlFor='oe-end'
                className='mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground'
              >
                End date
              </label>
              <Input
                id='oe-end'
                type='date'
                className='h-9 w-44'
                value={draftEnd}
                disabled={mutation.isPending}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? 'oe-range-error' : undefined}
                onChange={(e) => setDraftEnd(e.target.value)}
              />
            </div>
            <div className='flex items-center gap-2'>
              <Button size='sm' onClick={save} disabled={mutation.isPending}>
                {mutation.isPending ? (
                  <Loader2 className='mr-1 h-3 w-3 animate-spin' />
                ) : (
                  <Check className='mr-1 h-3 w-3' />
                )}
                Save
              </Button>
              <Button variant='ghost' size='sm' onClick={cancel} disabled={mutation.isPending}>
                <X className='mr-1 h-3 w-3' />
                Cancel
              </Button>
            </div>
          </div>
          {error ? (
            <p id='oe-range-error' role='alert' className='text-sm text-red-600'>
              {error}
            </p>
          ) : null}
        </div>
      ) : (
        <div className='space-y-1'>
          {range ? (
            <p className='text-sm font-medium text-foreground'>{range}</p>
          ) : status === 'INCOMPLETE' ? (
            <p className='text-sm text-foreground'>
              {startDate
                ? `Starts ${formatDate(startDate, 'MMM D, YYYY')} — end date not set`
                : `Ends ${formatDate(endDate, 'MMM D, YYYY')} — start date not set`}
            </p>
          ) : (
            <p className='text-sm text-muted-foreground'>No open enrollment window set.</p>
          )}
          <p className='text-xs text-muted-foreground'>
            {status === 'ACTIVE'
              ? 'Badged on this account and in the accounts list for brokers, admins, and CBS.'
              : status === 'UPCOMING'
                ? 'Badged as upcoming on this account and in the accounts list until it opens.'
                : status === 'INCOMPLETE'
                  ? 'Both dates are needed before the badge appears on the account.'
                  : 'Setting a window badges this account here and in the accounts list.'}
          </p>
        </div>
      )}
    </Card>
  );
}
